/* eslint-disable import/no-named-as-default-member */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-param-reassign */
import axios from 'axios';
import envConfig from '../../../config/currentEnv';
import { redirectToAuth } from '../../../utils/redirect';
import packageJson from '../../../../package.json';
import { forgeAndSendCookieToNative, isWebView, isWebViewAndroid, isWebViewIOS } from '../../../utils/webview';
import { AXIOS_TIMEOUT, REFRESH_TOKEN_MIN_INTERVAL } from '../../../utils/constants';
import { asyncStorageApi, KEY_WV_MOBILE_VERSION } from '../../../localStorageAPI';

const EXPECTED_ERROR_CODE = 401;
const JWT_REFRESH_BEFORE: number = ['development', 'staging'].includes(envConfig.currentEnv.env) ? 1 * 60 : 5 * 60; // 5 Minutes (prod) otherwise 1 minute
// Margin on top of the expiration, so a token that is about to die is not considered usable.
// This is NOT about the proactive renew (JWT_REFRESH_BEFORE handles that scheduling): it covers
// the case where the scheduled renew never ran, typically a webview whose timers were suspended
// while backgrounded. Saves a doomed request + 401 + refresh + replay. Renewing early is free,
// /auth/renew accepts an expired JWT anyway.
const JWT_EXPIRY_MARGIN = 30; // seconds
const TOKEN_POLL_INTERVAL = 50; // ms between two document.cookie reads while waiting for a token

// ===============================//
// =========== JWT/TOKEN =========//
// ===============================//

export const getJWTRefresh = (token: string = getToken()): number => {
    if (!token) {
        console.log(`getJWTRefresh no token`);
        return -1;
    }
    const datas = parseJWT(token);
    if (!datas) {
        console.log(`getJWTRefresh unparseable token`);
        return -1;
    }
    console.log(`getJWTRefresh token expiration ${datas.exp}, we will remove ${JWT_REFRESH_BEFORE} from this`);
    return Math.max(datas.exp - Date.now() / 1000 - JWT_REFRESH_BEFORE, 0) * 1000;
};

export const isValidJWT = (token: string = getToken()): boolean => {
    if (!token) {
        return false;
    }
    const datas = parseJWT(token);
    if (!datas) {
        return false;
    }
    return datas.exp > Date.now() / 1000 + JWT_EXPIRY_MARGIN;
};

// Never throws: a truncated or empty `token` cookie (webviews and stale deletions do produce
// those) used to blow up every getToken() caller, including the axios request interceptor.
const parseJWT = (token: string): { exp: number } | null => {
    try {
        const base64Url = token?.split('.')[1];
        if (!base64Url) {
            return null;
        }
        const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
        const jsonPayload = decodeURIComponent(
            window
                .atob(base64)
                .split('')
                .map(function parse(c) {
                    return `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`;
                })
                .join(''),
        );

        const payload = JSON.parse(jsonPayload);
        return typeof payload?.exp === 'number' ? payload : null;
    } catch (error) {
        console.log('parseJWT: unparseable token', error?.message);
        return null;
    }
};

// Reads every value for a given cookie name. `document.cookie` can hold duplicates (a
// domain-scoped one plus a host-only one) so we cannot rely on Cookies.get, which only
// returns the first match. Splits on the first `=` only: values may contain `=`.
const readCookieValues = (name: string): string[] => {
    const values: string[] = [];
    for (const cookie of document.cookie.split(/;\s*/)) {
        const separator = cookie.indexOf('=');
        if (separator < 0) {
            continue;
        }
        if (cookie.slice(0, separator).trim() !== name) {
            continue;
        }
        const value = decodeURIComponent(cookie.slice(separator + 1));
        if (value) {
            values.push(value);
        }
    }
    return values;
};

export const getLatestToken = (searchKey = 'token'): string => {
    const tokenValues = readCookieValues(searchKey);

    if (tokenValues.length === 0) {
        return null; // No tokens found
    }
    if (tokenValues.length > 1) {
        console.log(`Multiple ${searchKey} cookies found (${tokenValues.length})`);
    }

    let latestToken: string = null;
    let maxExp = -Infinity;
    for (const value of tokenValues) {
        const exp = parseJWT(value)?.exp;
        if (exp === undefined) {
            continue; // unparseable leftover cookie, ignore it
        }
        if (exp > maxExp) {
            latestToken = value;
            maxExp = exp;
        }
    }

    return latestToken;
};

export const getToken = (key = 'token'): string => {
    // console.log('debug Latest', key, ' => ', getLatestToken(key));
    return getLatestToken(key);
};

export const hasToken = (key = 'token'): boolean => {
    return !!getToken(key);
};

// Waits for a usable token to show up in document.cookie, polling instead of blocking.
// Needed in the native webview: the cookie can land slightly after the renew response
// (and at cold start it is injected by the app, not by us).
export const waitForToken = async (previousToken: string = null, timeoutMs = 3000): Promise<string> => {
    const deadline = Date.now() + timeoutMs;
    let token = getToken();
    while ((!token || token === previousToken) && Date.now() < deadline) {
        await new Promise((resolve) => setTimeout(resolve, TOKEN_POLL_INTERVAL));
        token = getToken();
    }
    if (!token || token === previousToken) {
        console.log(`waitForToken: gave up after ${timeoutMs}ms (token present: ${!!token})`);
        return null;
    }
    return token;
};

// =================================//
// =========== Axios Confs =========//
// =================================//

// Resolved per request: the native app can write the mobile version to localStorage after the
// bundle has been evaluated, so reading it once at module load froze it to 'UKN'.
const getWebViewHeaders = () => {
    if (!isWebView()) {
        return {};
    }
    return {
        'Mobile-Device': isWebViewIOS() ? 'iOS' : isWebViewAndroid() ? 'Android' : 'UKN',
        'Mobile-Version': asyncStorageApi.restoreDataNoLink(KEY_WV_MOBILE_VERSION) ?? 'UKN',
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        Pragma: 'no-cache',
        Expires: '0',
    };
};

const getDefaultHeaders = () => {
    return {
        'App-Version': packageJson.version,
        'Content-Type': 'application/json',
        ...getWebViewHeaders(),
    };
};

const customParamsSerializer = (params: Record<string, any>): string => {
    const queryParts: string[] = [];

    for (const [key, value] of Object.entries(params)) {
        if (value === undefined || value === null) continue;

        if (Array.isArray(value)) {
            value.forEach((item) => {
                if (typeof item === 'object') {
                    queryParts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(JSON.stringify(item))}`);
                } else {
                    queryParts.push(`${encodeURIComponent(key)}[]=${encodeURIComponent(item)}`);
                }
            });
        } else if (typeof value === 'object') {
            queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(JSON.stringify(value))}`);
        } else {
            queryParts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
        }
    }

    return queryParts.join('&');
};

// Attaches the token to every request. Only the webview headers are refreshed here: the rest
// of the defaults stay on the instance, so per-request Content-Type (multipart uploads) is kept.
const attachToken = async (config) => {
    config.withCredentials = true;
    for (const [name, value] of Object.entries(getWebViewHeaders())) {
        config.headers[name] = value;
    }

    let token = getToken();
    if (!token && isWebView()) {
        // Cold start in the native webview: the app may not have injected the cookie yet.
        // Redirecting here would kick a legitimately logged-in user back to auth.
        token = await waitForToken();
    }

    if (!token) {
        //redirectToAuth('Code - pYWQsSOM'); // Disabled to lower sentry spamming
        redirectToAuth();

        return Promise.reject(new Error('Request canceled - no token'));
    }

    config.headers.Token = token;
    return config;
};

// Request Private Api
export const axiosInstance = axios.create({
    baseURL: envConfig.currentEnv.apiURL,
    headers: getDefaultHeaders(),
    timeout: AXIOS_TIMEOUT,
    withCredentials: true,
    paramsSerializer: customParamsSerializer,
});

axiosInstance.interceptors.request.use(attachToken, (error) => {
    return Promise.reject(error);
});

// Request ID
export const axiosCustomInstance = axios.create({
    baseURL: envConfig.currentEnv.idURL,
    headers: getDefaultHeaders(),
    timeout: AXIOS_TIMEOUT,
    withCredentials: true,
    paramsSerializer: customParamsSerializer,
});

axiosCustomInstance.interceptors.request.use(attachToken, (error) => {
    return Promise.reject(error);
});

const refreshTokenState = { lastCall: 0, lastPromise: null, lastResponse: null };

// Function to refresh the access token using your refresh token API endpoint
export const refreshToken = async () => {
    // True single-flight, whatever the timing. Two concurrent /auth/renew calls made with an
    // already-expired JWT are fatal: ID consumes (deletes) the redis long_token on the first
    // one, so the second gets a 401 + clearCookie('token') and the user is logged out.
    if (refreshTokenState.lastPromise) {
        console.log('refreshToken: joining the renew already in flight.');
        return refreshTokenState.lastPromise;
    }

    const now = Date.now();
    if (now - refreshTokenState.lastCall < REFRESH_TOKEN_MIN_INTERVAL) {
        console.log('refreshToken: called too early, returning cached response.');
        if (
            refreshTokenState.lastResponse instanceof Error ||
            (refreshTokenState.lastResponse && refreshTokenState.lastResponse.isAxiosError)
        ) {
            console.log('refreshToken: lastResponse is AxiosError.');
            return Promise.reject(refreshTokenState.lastResponse);
        }
        console.log('refreshToken: lastResponse.');
        return Promise.resolve(refreshTokenState.lastResponse);
    }
    refreshTokenState.lastCall = now;
    refreshTokenState.lastPromise = (async () => {
        try {
            console.log('refreshing token');
            let token = getToken();
            if (!token && isWebView()) {
                // Cold start: the native app injects the cookie, and /auth/renew authenticates
                // purely from it (sent via withCredentials - this call does not go through the
                // instances, so there is no Token header to fall back on). Renewing before the
                // injection lands 401s and signs a legitimately connected user out at launch.
                console.log('refreshToken: no cookie yet, waiting for the native app to inject it');
                token = await waitForToken();
            }
            console.log(`current token : ${token}`);
            // Make an API call to your server to refresh the access token
            const res = await axios.get(`${envConfig.currentEnv.idURL}/auth/renew`, {
                withCredentials: true, // Needed to send cookies
                timeout: AXIOS_TIMEOUT,
                headers: { ...getDefaultHeaders() },
            });

            if (res.headers['set-cookie']) {
                console.log('refreshToken Set-Cookie:', res.headers['set-cookie']);
            }

            if (isWebView()) {
                // The webview cannot rely on the Set-Cookie of this response (cross-site
                // cookie write), so we forge it client-side and hand it to the native app.
                forgeAndSendCookieToNative(res);
                const stored = await waitForToken(token);
                console.log(`refreshToken: forged cookie readable ? ${!!stored}`);
            }
            refreshTokenState.lastResponse = res;
            return res;
        } catch (error) {
            // Check if we didn't renew somehow before
            if (getJWTRefresh() <= 0) {
                console.log(`Expired token : ${getToken()}`);
                redirectToAuth('Code - weSJ0nmU', error);
            } else {
                console.log(`Renew SAVED from redirect with current token : ${getToken()}`);
            }
            refreshTokenState.lastResponse = error;
            throw error;
        } finally {
            refreshTokenState.lastPromise = null;
        }
    })();
    return refreshTokenState.lastPromise;
};

// Create variables to track the token refreshing process
let isRefreshing = false;
const refreshSubscribers: Array<(error?: any) => void> = [];

// Function to add requests to the refreshSubscribers array
const subscribeTokenRefresh = (callback: (error?: any) => void) => {
    refreshSubscribers.push(callback);
};

// Replays (or fails) the requests queued during a refresh. The queue MUST be drained: keeping
// the callbacks around made every later refresh replay all the previously queued requests too,
// and a failed refresh left them pending forever.
const onRefreshed = (error?: any) => {
    const subscribers = refreshSubscribers.splice(0, refreshSubscribers.length);
    subscribers.forEach((callback) => callback(error));
};

function createResponseInterceptor(instance, options) {
    const { logPrefix = '', retryAuthCode = '', retryAuthCodeAlreadyRetried = '' } = options || {};
    return [
        (response) => {
            if (response.headers['set-cookie']) {
                console.log(`${logPrefix} Set-Cookie:`, response.headers['set-cookie']);
            }
            console.log(`${logPrefix} Response`, response);

            return response;
        },
        async (error) => {
            const originalRequest = error.config;
            console.log(`${logPrefix} interceptors response`, error.response?.status, originalRequest?._retry);
            // Check if the error is related to expired token
            // eslint-disable-next-line no-underscore-dangle
            if (error.response && error.response.status === EXPECTED_ERROR_CODE && !originalRequest?._retry) {
                originalRequest._retry = true;

                console.log(`${logPrefix} Retrying - isRefreshing ? `, isRefreshing);
                // Create a promise to retry the original request with the new access token
                const retryOriginalRequest = new Promise((resolve, reject) => {
                    subscribeTokenRefresh((refreshError?: any) => {
                        if (refreshError) {
                            reject(refreshError);
                        } else {
                            resolve(instance(originalRequest));
                        }
                    });
                });
                if (!isRefreshing) {
                    isRefreshing = true;

                    console.log(`${logPrefix} Refreshing`);
                    try {
                        await refreshToken();
                        isRefreshing = false;
                        onRefreshed();
                    } catch (refreshError) {
                        // Handle refresh token failure (e.g., redirect to login page)
                        isRefreshing = false;
                        onRefreshed(refreshError);
                        redirectToAuth(retryAuthCode, refreshError);
                    }
                }

                return retryOriginalRequest;
            }
            if (error.response && error.response.status === EXPECTED_ERROR_CODE && originalRequest?._retry) {
                redirectToAuth(retryAuthCodeAlreadyRetried, error);
            }

            return Promise.reject(error);
        },
    ];
}

axiosCustomInstance.interceptors.response.use(
    ...createResponseInterceptor(axiosCustomInstance, {
        logPrefix: 'axiosCustom',
        retryAuthCode: 'Code - M3LUCU0h',
        retryAuthCodeAlreadyRetried: 'Code - PqnqpzeC',
    }),
);

axiosInstance.interceptors.response.use(
    ...createResponseInterceptor(axiosInstance, {
        logPrefix: 'axiosInstance',
        retryAuthCode: 'Code - SQiWzAD4',
        retryAuthCodeAlreadyRetried: 'Code - TWuMzBCe',
    }),
);
